/**
 * Homepage hero slider.
 *
 * Markup comes from Elements/newsstudios/FRONT_A.ctp; the dots are skinned in
 * _v2/css/src/components.css.
 *
 * WHY SPLIDE AND NOT THE MOCKUP'S HAND-WRITTEN CROSSFADE. The mockup toggles an
 * `.active` class across absolutely-positioned slides — about 40 lines, and it
 * has no swipe, no keyboard support, no ARIA, and no pause on hover or focus.
 * A full-bleed hero on a phone is the one element people expect to swipe.
 * Splide is already the carousel every other tenant in this fleet uses
 * (base_neo's FRONT_A), so this is the conventional choice as well as the
 * better one.
 *
 * `type: 'fade'` reproduces the mockup's crossfade exactly; Splide keeps the
 * slides stacked and animates opacity, which is what the hand-written version
 * did by hand.
 */
import Splide from '@splidejs/splide';
import { qs, prefersReducedMotion } from './dom.js';
import { startAutoplayOnInput } from './autoplay.js';

export function setupHero() {
  const root = qs('[data-hero]');
  if (!root) return;

  // A single slide needs no carousel — and Splide would still render
  // pagination for it.
  const slides = root.querySelectorAll('.splide__slide');
  if (slides.length < 2) {
    root.classList.add('is-single');
    return;
  }

  const reduced = prefersReducedMotion();

  const splide = new Splide(root, {
    type: 'fade',
    rewind: true,
    // Fade needs one slide per page; anything else stacks two at once.
    perPage: 1,
    /*
     * READ FROM THE DOCUMENT, never hardcoded.
     *
     * XCMS serves several languages from one build and puts the direction on
     * <html dir> (XCMS_DIRECTION). Splide uses this for which way a swipe
     * advances and which side "previous" is on, so a hardcoded 'rtl' means the
     * English site's slider swipes backwards — a bug nobody would find on the
     * Persian site, which is the one anybody tests.
     */
    direction: document.documentElement.getAttribute('dir') === 'ltr' ? 'ltr' : 'rtl',
    /*
     * Arrows are POSITIONED as well as skinned in components.css — the `core`
     * stylesheet this build imports has no arrow rules whatsoever. Do not turn
     * these on in another slider without carrying those rules across.
     */
    arrows: true,
    pagination: true,
    // Reduced motion: no autoplay and no crossfade. The slider still works,
    // it just does not move on its own.
    //
    // 'pause' mounts autoplay stopped; the first input starts it — see
    // lib/autoplay.js for why (LCP).
    autoplay: reduced ? false : 'pause',
    interval: 4000,
    speed: reduced ? 0 : 600,
    pauseOnHover: true,
    pauseOnFocus: true,
    // Screen-reader labels. Splide's defaults are English; these are the
    // strings the rest of the UI uses.
    /*
     * `first` and `last` are not padding. With `rewind: true` Splide swaps the
     * arrow's aria-label for those two keys at the ends of the carousel — the
     * next button on the last slide really does go to the first — and anything
     * not listed here falls back to Splide's ENGLISH default. So on the last
     * slide of the Persian homepage the button announced "Go to first slide".
     */
    i18n: {
      prev: 'اسلاید قبلی',
      next: 'اسلاید بعدی',
      first: 'رفتن به اسلاید اول',
      last: 'رفتن به اسلاید آخر',
      slideX: 'رفتن به اسلاید %s',
      carousel: 'اسلایدر خبرهای اصلی',
      slide: 'اسلاید',
      slideLabel: 'اسلاید %s از %s',
      select: 'انتخاب اسلاید',
    },
  });

  splide.mount();
  if (!reduced) startAutoplayOnInput(root, splide);
  return splide;
}
